import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";

import { trackTranscriptionCompleted } from "../lib/analytics";
import { DEFAULT_ESTIMATED_SEC } from "../lib/transcriptionProgress";
import { useAuth } from "./AuthContext";

const API_BASE = "https://api.echonour.com";
const POLL_INTERVAL = 2500;
const MAX_RETRIES = 3;
const RETRY_DELAY = 3000;
const SESSION_KEY = "tempo_active_jobs";
const BALANCE_REFRESH_EVENT = "echonour:balance-refresh";

// ── Types ──

export interface TranscriptionJob {
  jobId: string;
  title: string;
  status: "polling" | "completed" | "failed";
  stage: string;
  progress: number; // 0-100; for polling jobs this is unused — components compute via computeProgress()
  resultItemId?: string; // library item id when completed
  error?: string;
  addedAt: number; // Date.now()
  startedAt: number; // Date.now() when job was created
  estimatedDurationSec: number; // estimated total processing seconds (audio_seconds / PROCESSING_SPEED_FACTOR)
  hidden?: boolean; // hidden from bottom bar (X clicked) but still polling
  completedAt?: number; // timestamp when job completed (for auto-cleanup)
}

interface TranscriptionContextValue {
  jobs: TranscriptionJob[];
  addJob: (jobId: string, title: string, estimatedDurationSec?: number) => void;
  addBatchJob: (jobId: string, title: string) => void; // add without individual polling (for batch items)
  addBatchPoll: (batchId: string, items: Array<{ id: string; title?: string }>) => void; // register batch items + start GET /batch/{id} poller
  updateJob: (jobId: string, updates: Partial<Pick<TranscriptionJob, 'status' | 'stage' | 'progress' | 'resultItemId' | 'error'>>) => void;
  addCompletedJob: (jobId: string, title: string, resultItemId?: string) => void; // for cache hits
  hideJob: (jobId: string) => void; // hide bar, keep polling
  removeJob: (jobId: string) => void; // fully remove + stop polling
  removeAll: () => void;
}

const TranscriptionContext = createContext<TranscriptionContextValue | null>(
  null,
);

// ── Helpers ──

function loadFromSession(): TranscriptionJob[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return [];
    const jobs: TranscriptionJob[] = JSON.parse(raw);
    // Resume any that were still polling. Default estimatedDurationSec for jobs
    // serialised before this field was introduced.
    return jobs.map((j) =>
      j.status === "polling"
        ? {
            ...j,
            stage: "Reconnecting...",
            startedAt: j.startedAt ?? Date.now(),
            estimatedDurationSec: j.estimatedDurationSec ?? DEFAULT_ESTIMATED_SEC,
          }
        : {
            ...j,
            startedAt: j.startedAt ?? Date.now(),
            estimatedDurationSec: j.estimatedDurationSec ?? DEFAULT_ESTIMATED_SEC,
          },
    );
  } catch {
    return [];
  }
}

function saveToSession(jobs: TranscriptionJob[]) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(jobs));
  } catch {
    // quota exceeded — ignore
  }
}

function notifyBalanceChanged() {
  window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
}

// ── Provider ──

export function TranscriptionProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<TranscriptionJob[]>(loadFromSession);
  const { refreshBalance } = useAuth();
  // Deduplicate doorbell rings per jobId across batch-poll cycles. The
  // batch poll body iterates every POLL_INTERVAL (2.5s) and updateJob is
  // called per-item per-poll — without dedupe we would ring the doorbell
  // repeatedly for the same completed item until the batch clears. Single-
  // job poller (pollJob) and addCompletedJob are single-fire by construction
  // and DO NOT need this.
  const completedNotifiedRef = useRef<Set<string>>(new Set());
  const intervalsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const retryCountsRef = useRef<Map<string, number>>(new Map());
  // batch-level pollers, keyed by batch_id (separate from per-job intervalsRef)
  const batchPollersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );

  // Persist to session whenever jobs change
  useEffect(() => {
    saveToSession(jobs);
  }, [jobs]);

  // Poll a single job
  const pollJob = useCallback((jobId: string) => {
    const token = localStorage.getItem("tempo_token");

    const doPoll = () => {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE}/job/${jobId}`, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          if (res.status === 401) {
            clearInterval(interval);
            intervalsRef.current.delete(jobId);
            setJobs((prev) =>
              prev.map((j) =>
                j.jobId === jobId
                  ? { ...j, status: "failed", error: "Session expired" }
                  : j,
              ),
            );
            return;
          }

          const data = await res.json();

          // Reset retry counter on success
          retryCountsRef.current.set(jobId, 0);

          const stage = data.message || "Processing...";

          if (data.status === "not_found") {
            clearInterval(interval);
            intervalsRef.current.delete(jobId);
            setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
            return;
          }

          if (data.status === "completed") {
            clearInterval(interval);
            intervalsRef.current.delete(jobId);
            notifyBalanceChanged();
            const resultItemId =
              data.result?.item_id || data.result?.saved_item_id || data.result?.id || data.item_id;
            
            // Track completion
            const durationMin = data.result?.duration_ms 
              ? Math.round(data.result.duration_ms / 60000) 
              : undefined;
            trackTranscriptionCompleted(durationMin);
            
            setJobs((prev) =>
              prev.map((j) =>
                j.jobId === jobId
                  ? {
                      ...j,
                      status: "completed",
                      progress: 100,
                      stage: "Completed",
                      title: data.result?.title || j.title,
                      resultItemId,
                      completedAt: Date.now(),
                    }
                  : j,
              ),
            );
            refreshBalance();
            return;
          }

          if (data.status === "failed") {
            clearInterval(interval);
            intervalsRef.current.delete(jobId);
            notifyBalanceChanged();
            // Check for friendly error_type messages
            const FRIENDLY: Record<string, string> = {
              video_unavailable:
                "YouTube prevents automated downloads of some videos.",
              service_busy:
                "YouTube service is temporarily busy. Try again shortly.",
              bot_gate:
                "YouTube is currently blocking automated downloads of this video. Please try again later or try a different video.",
            };
            const errorType = data.error_type || data.error?.error_type;
            const errMsg =
              (typeof errorType === "string" && FRIENDLY[errorType]) ||
              (typeof data.error === "string"
                ? data.error
                : data.error?.message || "Transcription failed");
            setJobs((prev) =>
              prev.map((j) =>
                j.jobId === jobId
                  ? { ...j, status: "failed", error: errMsg, stage: "Failed", completedAt: Date.now() }
                  : j,
              ),
            );
            return;
          }

          // Still processing — update only `stage`. Progress is computed at
          // render time via computeProgress(startedAt, estimatedDurationSec);
          // no longer stored in state for polling jobs.
          setJobs((prev) =>
            prev.map((j) => (j.jobId === jobId ? { ...j, stage } : j)),
          );
        } catch {
          clearInterval(interval);
          intervalsRef.current.delete(jobId);

          const retries = (retryCountsRef.current.get(jobId) ?? 0) + 1;
          retryCountsRef.current.set(jobId, retries);

          if (retries <= MAX_RETRIES) {
            setJobs((prev) =>
              prev.map((j) =>
                j.jobId === jobId
                  ? {
                      ...j,
                      stage: `Connection lost — retrying (${retries}/${MAX_RETRIES})...`,
                    }
                  : j,
              ),
            );
            setTimeout(() => doPoll(), RETRY_DELAY);
          } else {
            notifyBalanceChanged();
            setJobs((prev) =>
              prev.map((j) =>
                j.jobId === jobId
                  ? {
                      ...j,
                      status: "failed",
                      error: "Connection lost",
                      stage: "Connection lost",
                      completedAt: Date.now(),
                    }
                  : j,
              ),
            );
          }
        }
      }, POLL_INTERVAL);

      intervalsRef.current.set(jobId, interval);
    };

    doPoll();
  }, []);

  // Start polling for any jobs that need it on mount (restored from session)
  useEffect(() => {
    jobs.forEach((job) => {
      if (job.status === "polling" && !intervalsRef.current.has(job.jobId)) {
        pollJob(job.jobId);
      }
    });
    // Only run once on mount
  }, []);

  // Cleanup intervals on unmount
  useEffect(() => {
    const intervals = intervalsRef.current;
    const batchIntervals = batchPollersRef.current;
    return () => {
      intervals.forEach((interval) => clearInterval(interval));
      intervals.clear();
      batchIntervals.forEach((interval) => clearInterval(interval));
      batchIntervals.clear();
    };
  }, []);

  // Auto-cleanup completed jobs after 5 minutes
  useEffect(() => {
    const CLEANUP_AFTER_MS = 5 * 60 * 1000; // 5 minutes
    const timer = setInterval(() => {
      const now = Date.now();
      setJobs((prev) => {
        const filtered = prev.filter((j) => {
          // Keep polling jobs
          if (j.status === "polling") return true;
          // Keep completed/failed jobs that are less than 5 minutes old
          if (j.completedAt && now - j.completedAt < CLEANUP_AFTER_MS) return true;
          // Keep jobs without completedAt (shouldn't happen, but safe)
          if (!j.completedAt && (j.status === "completed" || j.status === "failed")) return true;
          return false;
        });
        // Only update if something changed
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 30000); // Check every 30 seconds

    return () => clearInterval(timer);
  }, []);

  const addJob = useCallback(
    (jobId: string, title: string, estimatedDurationSec?: number) => {
      setJobs((prev) => {
        // Don't add duplicate
        if (prev.some((j) => j.jobId === jobId)) return prev;
        return [
          ...prev,
          {
            jobId,
            title: title || "Transcription",
            status: "polling",
            stage: "Submitted",
            progress: 0,
            addedAt: Date.now(),
            startedAt: Date.now(),
            estimatedDurationSec: estimatedDurationSec ?? DEFAULT_ESTIMATED_SEC,
          },
        ];
      });
      notifyBalanceChanged();
      if (!intervalsRef.current.has(jobId)) {
        pollJob(jobId);
      }
    },
    [pollJob],
  );

  // Add a batch job (no individual polling - updates come from batch polling externally)
  const addBatchJob = useCallback(
    (jobId: string, title: string) => {
      setJobs((prev) => {
        if (prev.some((j) => j.jobId === jobId)) return prev;
        return [
          ...prev,
          {
            jobId,
            title: title || "Transcription",
            status: "polling",
            stage: "Queued",
            progress: 0,
            addedAt: Date.now(),
            startedAt: Date.now(),
            estimatedDurationSec: DEFAULT_ESTIMATED_SEC,
          },
        ];
      });
      notifyBalanceChanged();
    },
    [],
  );

  // Update job state externally (for batch items)
  const updateJob = useCallback(
    (jobId: string, updates: Partial<Pick<TranscriptionJob, 'status' | 'stage' | 'progress' | 'resultItemId' | 'error'>>) => {
      if (updates.status === 'completed' || updates.status === 'failed') {
        notifyBalanceChanged();
      }
      setJobs((prev) =>
        prev.map((j) => {
          if (j.jobId !== jobId) return j;
          const updated = { ...j, ...updates };
          // Set completedAt if transitioning to completed/failed
          if ((updates.status === 'completed' || updates.status === 'failed') && !j.completedAt) {
            updated.completedAt = Date.now();
          }
          return updated;
        }),
      );
    },
    [],
  );

  // Add a batch and start polling GET /batch/{batchId} to drive item updates.
  // Survives navigation because polling lives in the context (the bottom bar
  // remains mounted across routes). Resilient to duplicate registration.
  const addBatchPoll = useCallback(
    (batchId: string, items: Array<{ id: string; title?: string }>) => {
      // Register all items in the context as polling (no per-/job/{id} polling)
      setJobs((prev) => {
        const existingIds = new Set(prev.map((j) => j.jobId));
        const additions: TranscriptionJob[] = items
          .filter((it) => !existingIds.has(it.id))
          .map((it) => ({
            jobId: it.id,
            title: it.title || "Transcription",
            status: "polling" as const,
            stage: "Queued",
            progress: 0,
            addedAt: Date.now(),
            startedAt: Date.now(),
            estimatedDurationSec: DEFAULT_ESTIMATED_SEC,
          }));
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });
      notifyBalanceChanged();

      // Don't double-register a poller for the same batch
      if (batchPollersRef.current.has(batchId)) return;

      const token = localStorage.getItem("tempo_token");
      let retries = 0;

      const STATUS_MAP: Record<string, "polling" | "completed" | "failed"> = {
        queued: "polling",
        processing: "polling",
        completed: "completed",
        cached: "completed",
        failed: "failed",
      };
      const STAGE_MAP: Record<string, string> = {
        queued: "Queued",
        processing: "Transcribing...",
        completed: "Completed",
        cached: "Completed (cached)",
        failed: "Failed",
      };

      const poll = async () => {
        try {
          const res = await fetch(`${API_BASE}/batch/${batchId}`, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          retries = 0;
          const itemList: Array<{
            id: string;
            status: string;
            progress?: number;
            result_item_id?: string;
            error?: string | null;
          }> = Array.isArray(data.items) ? data.items : [];

          itemList.forEach((it) => {
            const status = STATUS_MAP[it.status] || "polling";
            const progress =
              it.progress != null
                ? Math.round(it.progress * 100)
                : status === "completed"
                  ? 100
                  : status === "polling"
                    ? 50
                    : 0;
            const stage = STAGE_MAP[it.status] || it.status || "";
            updateJob(it.id, {
              status,
              progress,
              stage,
              resultItemId: it.result_item_id,
              error: it.error ?? undefined,
            });
            if (status === "completed" && !completedNotifiedRef.current.has(it.id)) {
              completedNotifiedRef.current.add(it.id);
              refreshBalance();
            }
          });

          const allDone =
            itemList.length > 0 &&
            itemList.every((it) =>
              ["completed", "cached", "failed"].includes(it.status),
            );
          if (allDone) {
            const interval = batchPollersRef.current.get(batchId);
            if (interval) clearInterval(interval);
            batchPollersRef.current.delete(batchId);
          }
        } catch {
          retries += 1;
          if (retries > MAX_RETRIES) {
            const interval = batchPollersRef.current.get(batchId);
            if (interval) clearInterval(interval);
            batchPollersRef.current.delete(batchId);
            items.forEach((it) =>
              updateJob(it.id, {
                status: "failed",
                stage: "Connection lost",
                error: "Connection lost",
              }),
            );
          }
        }
      };

      // Fire an immediate poll, then begin the interval
      poll();
      const interval = setInterval(poll, POLL_INTERVAL);
      batchPollersRef.current.set(batchId, interval);
    },
    [updateJob],
  );

  // Add a job that's already completed (e.g. cache hits) — triggers notification without polling
  const addCompletedJob = useCallback(
    (jobId: string, title: string, resultItemId?: string) => {
      setJobs((prev) => {
        // Don't add duplicate
        if (prev.some((j) => j.jobId === jobId)) return prev;
        return [
          ...prev,
          {
            jobId,
            title: title || "Transcription",
            status: "completed",
            stage: "Completed",
            progress: 100,
            resultItemId,
            addedAt: Date.now(),
            startedAt: Date.now(),
            estimatedDurationSec: DEFAULT_ESTIMATED_SEC,
            completedAt: Date.now(),
          },
        ];
      });
      notifyBalanceChanged();
      refreshBalance();
    },
    [refreshBalance],
  );

  // Hide bar from UI but keep polling — job continues in background
  const hideJob = useCallback((jobId: string) => {
    setJobs((prev) =>
      prev.map((j) => (j.jobId === jobId ? { ...j, hidden: true } : j)),
    );
  }, []);

  // Fully remove job and stop polling
  const removeJob = useCallback((jobId: string) => {
    const interval = intervalsRef.current.get(jobId);
    if (interval) {
      clearInterval(interval);
      intervalsRef.current.delete(jobId);
    }
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  const removeAll = useCallback(() => {
    intervalsRef.current.forEach((interval) => clearInterval(interval));
    intervalsRef.current.clear();
    setJobs([]);
  }, []);

  return (
    <TranscriptionContext.Provider
      value={{ jobs, addJob, addBatchJob, addBatchPoll, updateJob, addCompletedJob, hideJob, removeJob, removeAll }}
    >
      {children}
    </TranscriptionContext.Provider>
  );
}

export function useTranscriptionJobs() {
  const ctx = useContext(TranscriptionContext);
  if (!ctx) {
    throw new Error(
      "useTranscriptionJobs must be used within TranscriptionProvider",
    );
  }
  return ctx;
}
